import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { apiErrorText, useReconcileBankStatementsMutation } from '@shared/api';
import { decodeStatement } from '@shared/lib/decodeStatement';
import { Button } from '@shared/ui/Button';
import { IconCheck, IconFolderOpen, IconLightbulb, IconX } from '@shared/ui/icons';
import { formatMoney } from './financierFormat';
import { NO_1C_DOCUMENTS, count1cDocuments } from './importFile';

interface Props {
  open: boolean;
  onClose: () => void;
  activeRegistryId?: string | null;
  onSuccess?: () => void;
}

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
  padding: 16px;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ModalBody = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const TextArea = styled.textarea`
  width: 100%;
  height: 160px;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 12px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #10b981;
  }
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  line-height: 1.4;
  display: flex;
  align-items: flex-start;
  gap: 6px;

  & > svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

const ResultCard = styled.div`
  padding: 14px;
  border-radius: 8px;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid #10b981;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
`;

const ResultTitle = styled.div`
  font-weight: 700;
  color: #10b981;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ModalFooter = styled.div`
  padding: 16px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ErrorBox = styled.div`
  padding: 8px 12px;
  border-radius: 6px;
  background: #ef444422;
  border: 1px solid #ef4444;
  color: #ef4444;
  font-size: 12px;
  white-space: pre-line;
`;

const FileInfo = styled.div<{ $warn: boolean }>`
  font-size: 12px;
  color: ${({ $warn, theme }) => ($warn ? theme.colors.status.warning : theme.colors.text.secondary)};
`;

export function ReconcileStatementsModal({ open, onClose, activeRegistryId, onSuccess }: Props) {
  const [reconcile, { isLoading }] = useReconcileBankStatementsMutation();
  const [statementText, setStatementText] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Выбранный файл выписки: имя и сколько в нём платёжных документов.
  const [fileInfo, setFileInfo] = useState<{ text: string; empty: boolean } | null>(null);
  const [result, setResult] = useState<{
    matchedCount: number;
    totalAmount: number;
    fullyPaid: number;
    partiallyPaid: number;
  } | null>(null);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  // Выписки банков чаще в Windows-1251, реже в UTF-8: читаем байты и определяем
  // кодировку (`decodeStatement`), иначе кириллица файла теряется.
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;
    setError(null);
    setResult(null);
    try {
      const text = decodeStatement(await file.arrayBuffer());
      const docs = count1cDocuments(text);
      // Выписка за день без операций — только раздел счёта: файл в порядке.
      const accountOnly = docs === 0 && text.includes('СекцияРасчСчет');
      setStatementText(text);
      setFileInfo({
        text:
          docs > 0
            ? `Файл «${file.name}»: платёжных документов в выписке — ${docs}`
            : accountOnly
              ? `Файл «${file.name}»: операций в выписке нет — сопоставлять с реестром нечего`
              : `Файл «${file.name}»: ${NO_1C_DOCUMENTS}`,
        empty: docs === 0 && !accountOnly,
      });
    } catch {
      setFileInfo(null);
      setError(`Файл «${file.name}» не прочитан — проверьте его и выберите снова`);
    } finally {
      input.value = '';
    }
  };

  const handleRunReconcile = async () => {
    try {
      setError(null);
      setResult(null);
      const res = await reconcile({
        statement_content: statementText.trim() || undefined,
        registry_id: activeRegistryId || undefined,
      }).unwrap();

      setResult({
        matchedCount: res.matched_payments_count,
        totalAmount: res.total_matched_paid_amount,
        fullyPaid: res.fully_paid_count,
        partiallyPaid: res.partially_paid_count,
      });
      if (onSuccess) onSuccess();
    } catch (err: unknown) {
      setError(apiErrorText(err, 'Не удалось сверить выписку с реестром'));
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle>Сверка банковских выписок с реестром платежей</ModalTitle>
          <Button $variant="ghost" $size="sm" onClick={onClose} aria-label="Закрыть">
            <IconX size={14} />
          </Button>
        </ModalHeader>

        <ModalBody>
          {error && <ErrorBox role="alert">{error}</ErrorBox>}

          {result && (
            <ResultCard>
              <ResultTitle>
                <IconCheck size={16} />
                Сверка успешно завершена!
              </ResultTitle>
              <div>Сопоставлено платежей: <b>{result.matchedCount}</b></div>
              <div>Сумма подтверждённой оплаты: <b>{formatMoney(result.totalAmount)}</b></div>
              <div>Полностью оплачено заявок: <b>{result.fullyPaid}</b></div>
              <div>Частично оплачено заявок: <b>{result.partiallyPaid}</b></div>
            </ResultCard>
          )}

          <Hint>
            Вы можете запустить автоматическую сверку по всем ранее загруженным банковским выпискам холдинга, либо вставить/загрузить свежий файл выписки 1CClientBankExchange ниже.
          </Hint>

          <div>
            <label
              style={{
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                fontWeight: 600,
                padding: '6px 12px',
                borderRadius: 6,
                background: '#334155',
                color: '#ffffff',
                marginBottom: 8,
              }}
            >
              <IconFolderOpen size={14} />
              Загрузить файл выписки (.txt / .csv)
              <input
                type="file"
                accept=".txt,.csv"
                style={{ display: 'none' }}
                onChange={(e) => void handleFileUpload(e)}
              />
            </label>
            {fileInfo && (
              <FileInfo $warn={fileInfo.empty} role="status">
                {fileInfo.text}
              </FileInfo>
            )}
          </div>

          <TextArea
            placeholder="Опционально: вставьте текст банковской выписки 1CClientBankExchange для одновременного импорта и сопоставления с реестром…"
            value={statementText}
            onChange={(e) => {
              setStatementText(e.target.value);
              setFileInfo(null);
            }}
          />

          <Hint>
            <IconLightbulb size={14} />
            <span>
              Сопоставление выполняется многокритериально: по совпадению номера заявки или счёта в назначении платежа, совпадению ИНН и суммы. При подтверждении заявка автоматически переходит в статус «Оплачено» или «Частично» с фиксацией номера платёжного поручения.
            </span>
          </Hint>
        </ModalBody>

        <ModalFooter>
          <Button $variant="secondary" onClick={onClose}>
            Закрыть
          </Button>
          <Button $variant="primary" onClick={handleRunReconcile} disabled={isLoading}>
            {isLoading ? 'Сверка…' : 'Запустить автосверку'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
}
